import type { PlanPart, TreatmentPlan } from "@dentacore/shared-types";

/**
 * Where a plan has got to, in one sentence.
 *
 * **One implementation, because four screens ask it**: the plans list, the plan
 * itself, the band on Home and the calendar's plan picker. Before this they
 * each worked it out from `status`, a column nothing ever advanced — so all
 * four agreed, and all four were wrong about every finished plan.
 *
 * Everything here is derived from the visits (TP29). Nothing reads a stored
 * flag, because there is no longer one to read.
 */
export interface Standing {
  /** Visits finished, out of however many the plan has. */
  doneCount: number;
  total: number;
  /**
   * The first visit that has not been completed — what happens next, and null
   * on a plan that is finished or has no visits yet.
   */
  next: PlanPart | null;
  /**
   * The next visit could be booked right now and has not been.
   *
   * **The one thing on this screen that means somebody has to act today.** Not
   * merely "unbooked": a visit queued behind an unfinished one is waiting its
   * turn, which is the plan working correctly.
   */
  needsDate: boolean;
  /** `Visit 2 of 3 · Tue 15 Sep, 12:00` — the same fact the pips draw. */
  summary: string;
}

export function standingOf(plan: TreatmentPlan, timezone = "UTC"): Standing {
  const total = plan.parts.length;
  const doneCount = plan.parts.filter((part) => part.completedAt !== null).length;
  const next = plan.parts.find((part) => part.completedAt === null) ?? null;

  const needsDate =
    plan.closedAt === null &&
    next !== null &&
    next.booking === null &&
    next.blockedByVisit === null &&
    next.items.length > 0;

  return { doneCount, total, next, needsDate, summary: summarise(plan, next, timezone) };
}

function summarise(plan: TreatmentPlan, next: PlanPart | null, timezone: string): string {
  if (plan.closedAt !== null) return `Closed ${dayOf(plan.closedAt, timezone)}`;
  if (plan.completedAt !== null) {
    return `Finished · last visit ${dayOf(plan.completedAt, timezone)}`;
  }
  if (next === null) return "No visits yet";

  const position = `Visit ${String(plan.parts.indexOf(next) + 1)} of ${String(plan.parts.length)}`;

  // **A later visit done first on the calendar** (PP-D9): its work waits,
  // unbilled, until this one is marked done.
  const ahead = doneAhead(plan);
  if (ahead !== null) {
    return `${position} · ${nameOf(ahead, plan)} done first — mark ${nameOf(next, plan)} done`;
  }
  if (next.booking !== null) return `${position} · ${whenOf(next.booking.startsAt, timezone)}`;
  if (next.items.length === 0) return `${position} · nothing in it yet`;
  // **Named, not numbered.** "after visit 1" is what the server would refuse
  // with, and saying it here means the explanation arrives before the click.
  if (next.blockedByVisit !== null) {
    return `${position} · after visit ${String(next.blockedByVisit)}`;
  }

  return `${position} · ${nameOf(next, plan)} needs a date`;
}

/**
 * **The first later visit whose appointment was completed while a visit in
 * front of it is still open** (PP-D9) — its steps wait, unbilled, until the
 * earlier one is marked done. Null when nothing ran ahead.
 */
export function doneAhead(plan: TreatmentPlan): PlanPart | null {
  const open = plan.parts.findIndex((part) => part.completedAt === null);
  if (open === -1) return null;
  return (
    plan.parts
      .slice(open + 1)
      .find((part) => part.completedAt === null && part.booking?.status === "COMPLETED") ?? null
  );
}

/** A visit's own name, or its number where nobody gave it one. */
export function nameOf(part: PlanPart, plan: TreatmentPlan): string {
  const named = part.title?.trim() ?? "";
  if (named !== "") return named;
  return `visit ${String(plan.parts.indexOf(part) + 1)}`;
}

/**
 * `Tue 15 Sep, 12:00` — **on the branch's clock, never the reader's**.
 *
 * `Intl` with an explicit zone rather than arithmetic on an offset, for the
 * reason the calendar grid gives: the offset is not a constant, and a clinic on
 * daylight saving moves twice a year.
 */
export function whenOf(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

/** `15 Sep` — the same instant when the hour does not matter. */
export function dayOf(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}
